import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AudiotracksCMS, Company, User } from "../../../types/types";
import TitleSubtitle from "../../../components/common/TitleSubtitle";
import { useGlobalState } from "../../../context/useGlobalState";
import {
    activateProduct,
    cloneProduct,
    deactivateProduct,
    deleteProduct,
    getAdminInfo,
    getPaginatedProductsCompany,
} from "../../../api";
import TextInput from "../../../components/ui/TextInput";
import Dropdown from "../../../components/common/Dropdown";
import AudioTourCard from "../../../components/cards/AudiotourCard";
import styles from "./Experiences.module.css";

const PAGE_LIMIT = 10;

const Audiotracks: React.FC = () => {
    const { data, loading, error, setToken, refetch } = useGlobalState();
    const [productName, setProductName] = useState<string>("");
    const [productNameDebounced, setProductNameDebounced] = useState<string>("");
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
    const [filteredCities, setFilteredCities] = useState<{ value: string; label: string }[]>([]);
    const [type, setType] = useState<string>("");
    const [products, setProducts] = useState<AudiotracksCMS[]>([]);
    const [filterCompanies, setFilterCompanies] = useState<Company[]>([]);
    const [listLoading, setListLoading] = useState<boolean>(false);
    const [loadingState] = useState<Record<string, boolean>>({});
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [user, setUser] = useState<User | null>(null);

    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            setToken(token);
        }
    }, [setToken]);

    const fetchUser = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setUser(null);
            return;
        }

        try {
            const response = await getAdminInfo({ token });
            if (!response) {
                console.error("Error fetching user info:");
            } else {
                setUser(response);
            }
        } catch (err) {
            console.error("Error fetching user:", err);
        }
    };

    useEffect(() => {
        fetchUser();
    }, []);

    useEffect(() => {
        refetch();
        // fetch-on-mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedCountry) {
            const citiesForCountry = data.locationCityOptions.filter((city: any) => city.countryId === selectedCountry);
            setFilteredCities(citiesForCountry);
        } else {
            setFilteredCities([]);
            setSelectedCity(null);
        }
    }, [selectedCountry, data.locationCityOptions]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setProductNameDebounced(productName);
        }, 400);
        return () => clearTimeout(timer);
    }, [productName]);

    useEffect(() => {
        setPage(1);
    }, [productNameDebounced, type, selectedCountry, selectedCity, selectedCompany]);

    const getProductsAPI = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }

        try {
            setListLoading(true);
            const response = await getPaginatedProductsCompany({
                token,
                page,
                limit: PAGE_LIMIT,
                name: productNameDebounced || undefined,
                type: type || undefined,
                countryId: selectedCountry || undefined,
                cityId: selectedCity || undefined,
                companyId: selectedCompany || undefined,
            });

            setProducts(response.products || []);
            setFilterCompanies(response.companies || []);
            setTotalCount(response.pagination?.totalCount ?? 0);
            setTotalPages(response.pagination?.totalPages ?? 1);
        } catch (err) {
            console.error("Failed to fetch products", err);
        } finally {
            setListLoading(false);
        }
    }, [page, productNameDebounced, type, selectedCountry, selectedCity, selectedCompany]);

    useEffect(() => {
        getProductsAPI();
    }, [getProductsAPI]);

    const distinctCompaniesOptions = useMemo(
        () =>
            filterCompanies.map((company) => ({
                label: company.name,
                value: company.id,
            })),
        [filterCompanies],
    );

    // Same windowing as DatabaseContent
    const visiblePages = useMemo(() => {
        const range: (number | string)[] = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            if (page > 3) range.push("...");
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                range.push(i);
            }
            if (page < totalPages - 2) range.push("...");
            range.push(totalPages);
        }

        return range;
    }, [page, totalPages]);

    const startItem = totalCount === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1;
    const endItem = Math.min(page * PAGE_LIMIT, totalCount);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const token = localStorage.getItem("token");

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <TitleSubtitle
                    title={t("audioTours")}
                    subtitle={t("adjustTheItineraryAndTheContentOfYourAudioTourHere")}
                />
            </div>

            <hr className={styles.rule} />

            <div className={styles.filters}>
                <TextInput
                    placeholder={t("filterByProductName")}
                    value={productName}
                    size={"small"}
                    onChange={(e) => setProductName(e.target.value)}
                />

                <Dropdown
                    label=""
                    value={type}
                    options={data.categories}
                    onChange={setType}
                    placeholder={t("selectAnAudioguideType")}
                    loading={loading}
                    error={error ? true : false}
                    isClearable
                />

                <Dropdown
                    label=""
                    options={data.locationCountryOptions}
                    onChange={(v) => {
                        setSelectedCountry(v || null);
                        if (!v) setSelectedCity(null);
                    }}
                    loading={loading}
                    error={error ? true : false}
                    placeholder={t("country")}
                    value={selectedCountry || ""}
                    isClearable
                />

                {selectedCountry && (
                    <Dropdown
                        label=""
                        options={filteredCities}
                        onChange={(v) => setSelectedCity(v || null)}
                        disabled={!selectedCountry}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("city")}
                        value={selectedCity || ""}
                        isClearable
                    />
                )}

                {user?.isOwner && (
                    <Dropdown
                        label=""
                        options={distinctCompaniesOptions}
                        onChange={(v) => setSelectedCompany(v || null)}
                        placeholder={t("company")}
                        value={selectedCompany || ""}
                        loading={loading}
                        error={error ? true : false}
                        isClearable
                    />
                )}
            </div>

            <div className={`${styles.list}${listLoading ? ` ${styles.listLoading}` : ""}`}>
                {products.length === 0 && !listLoading ? (
                    <p className={styles.empty}>{t("noProductsFound")}</p>
                ) : (
                    products.map((product) => (
                        <AudioTourCard
                            key={product.trackId}
                            trackId={product.trackId}
                            duration={product.duration}
                            picture={product.backgroundPicture}
                            title={product.name}
                            start={product.start}
                            isActive={product.isActive}
                            loading={loadingState[product.id] || false}
                            activateProduct={async () => {
                                await activateProduct({
                                    token: token || "",
                                    productId: product.id,
                                });
                                getProductsAPI();
                            }}
                            deactivateProduct={async () => {
                                await deactivateProduct({
                                    token: token || "",
                                    productId: product.id,
                                });
                                getProductsAPI();
                            }}
                            deleteProduct={async () => {
                                await deleteProduct({
                                    token: token || "",
                                    productId: product.id,
                                });
                                getProductsAPI();
                            }}
                            cloneProduct={async () => {
                                await cloneProduct({ token: token || "", productId: product.id });
                                getProductsAPI();
                            }}
                        />
                    ))
                )}
            </div>

            {totalPages > 1 && (
                <div className={styles.paginationControls}>
                    <button
                        type="button"
                        disabled={page === 1 || listLoading}
                        onClick={() => handlePageChange(page - 1)}
                    >
                        ‹
                    </button>

                    {visiblePages.map((p, idx) =>
                        p === "..." ? (
                            <span key={`ellipsis-${idx}`} className={styles.paginationEllipsis}>
                                …
                            </span>
                        ) : (
                            <button
                                type="button"
                                key={p}
                                className={p === page ? styles.paginationActive : undefined}
                                disabled={listLoading}
                                onClick={() => handlePageChange(p as number)}
                            >
                                {p}
                            </button>
                        ),
                    )}

                    <button
                        type="button"
                        disabled={page === totalPages || listLoading}
                        onClick={() => handlePageChange(page + 1)}
                    >
                        ›
                    </button>

                    <span className={styles.paginationTotal}>
                        {t("displayedExperiences") || "Expériences affichées"}:{" "}
                        {`${startItem}–${endItem} / ${totalCount}`}
                    </span>
                </div>
            )}
        </div>
    );
};

export default Audiotracks;
